import type { IPeerConnection } from './types';

export type PreWarmHealthResult = {
  healthy: boolean;
  reason?: string;
};

export type IceFailureContext = {
  iceEverChecking: boolean;
  alreadyRestarted: boolean;
};

export type IceFailureAction =
  | { action: 'instant_retry' }
  | { action: 'restart' }
  | { action: 'skip'; reason: string };

/**
 * Validates pre-warmed RTCPeerConnections and provides ICE diagnostics.
 * Shared between host (PeerConnectionManager) and viewer (ClientPeerManager).
 */
export class IceHealthGuard {
  /**
   * Check whether a pre-warmed connection is still usable.
   * Returns unhealthy if the PC is too old or its ICE agent has
   * left the 'new' state (e.g. already failed or disconnected).
   */
  static checkPreWarm(
    connection: IPeerConnection,
    createdAt: number,
    maxAgeMs: number,
  ): PreWarmHealthResult {
    const age = Date.now() - createdAt;
    if (age > maxAgeMs) {
      return { healthy: false, reason: `too old (${Math.round(age / 1000)}s)` };
    }
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) {
      return { healthy: false, reason: 'no underlying RTCPeerConnection' };
    }
    const iceState = rtcPc.iceConnectionState;
    if (iceState !== 'new') {
      return { healthy: false, reason: `ICE state is '${iceState}', expected 'new'` };
    }
    return { healthy: true };
  }

  /**
   * Determine the appropriate recovery action when ICE fails.
   * - Instant failure (never reached 'checking') means the PC is unusable;
   *   a full reconnect with a fresh PC is needed.
   * - Normal failure after checking: attempt a single ICE restart.
   * - Already restarted: skip (let caller fall through to full reconnect).
   */
  static decideIceFailureAction(ctx: IceFailureContext): IceFailureAction {
    if (!ctx.iceEverChecking) {
      return { action: 'instant_retry' };
    }
    if (ctx.alreadyRestarted) {
      return { action: 'skip', reason: 'already restarted this epoch' };
    }
    return { action: 'restart' };
  }

  /**
   * Query getStats() after setRemoteDescription to verify that remote
   * candidates and candidate pairs were registered.
   * Returns counts for diagnostic logging.
   */
  static async getCandidateCounts(
    connection: IPeerConnection,
  ): Promise<{ remoteCandidates: number; candidatePairs: number } | null> {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) return null;
    try {
      const stats = await rtcPc.getStats();
      let remoteCandidates = 0;
      let candidatePairs = 0;
      stats.forEach((report) => {
        if (report.type === 'remote-candidate') remoteCandidates++;
        if (report.type === 'candidate-pair') candidatePairs++;
      });
      return { remoteCandidates, candidatePairs };
    } catch {
      return null;
    }
  }
}
